import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { API } from '../../../Helper';

const shimmerAnimation = keyframes`
  0% { background-position: -960px 0; }
  100% { background-position: 960px 0; }
`;

const ShimmerWrapper = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  overflow-x: hidden;
`;

const ShimmerSlide = styled.div`
  flex: 0 0 ${props => props.isMobile ? '100%' : '33.33%'};
  padding: 0 5px;
  box-sizing: border-box;
`;

const ShimmerContent = styled.div`
  width: 100%;
  max-width: 360px;
  height: 155px;
  border-radius: 12px;
  margin: 0 auto;
  background: linear-gradient(90deg, #292D2E 0%, #313737 50%, #292D2E 100%);
  background-size: 960px 100%;
  animation: ${shimmerAnimation} 1.5s infinite linear;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  padding: 15px 0;

  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

const SlideTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  transform: translateX(-${props => props.activeIndex * (props.isMobile ? 100 : 33.33)}%);
`;

const Slide = styled.div`
  flex: 0 0 ${props => props.isMobile ? '100%' : '33.33%'};
  padding: 0 5px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SlideContent = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  max-width: 360px;
  height: 155px;
  margin: 0 auto;
  cursor: ${props => props.hasLink ? 'pointer' : 'default'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: ${props => props.hasLink ? 'scale(1.02)' : 'none'};
    box-shadow: ${props => props.hasLink ? '0 4px 20px rgba(37, 238, 137, 0.25)' : 'none'};
  }
`;

const SlideImage = styled.img`
  width: 100%;
  height: 155px;
  max-width: 360px;
  object-fit: cover;
  opacity: 0.9;
  border-radius: 12px;
  display: block;
`;

const LinkBadge = styled.div`
  position: absolute;
  bottom: 10px;
  right: 12px;
  background: #232626;
  backdrop-filter: blur(6px);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 10px;
  pointer-events: none;
  letter-spacing: 0.3px;
`;

const PaginationDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? '#25ee89' : 'rgba(255,255,255,0.3)'};
  cursor: pointer;
  padding: 0;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: ${props => props.active ? '#25ee89' : '#888'};
  }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 155px;
  width: 100%;
  background: #232626;
  border-radius: 12px;
  color: #666;
  font-size: 14px;
`;

const SkylaPromoSlider = () => {
  const history = useHistory();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        // Use /list endpoint which returns image URLs (fast, no base64 payload)
        const response = await fetch(`${API}/api/promobanners/list`);
        const data = await response.json();

        if (data.status === 'success' && data.images && data.images.length > 0) {
          const processed = data.images.map(img => ({
            id: img.id,
            type: img.type,
            link: img.link || null,
            image: `${API}${img.imageUrl}`
          }));
          setBanners(processed);
        } else {
          setError('No promo banners available');
        }
      } catch (err) {
        console.error('Error fetching promo banners:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const totalSlides = loading
    ? 1
    : isMobile
      ? banners.length
      : Math.ceil(banners.length / 2);

  // Auto-scroll
  useEffect(() => {
    if (loading || banners.length === 0) return;

    const timer = setInterval(() => {
      setActiveIndex(current => (current + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(timer);
  }, [loading, totalSlides, banners.length]);

  const handleBannerClick = (link) => {
    if (!link) return;

    // External URL
    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      // Internal route
      history.push(link);
    }
  };

  if (loading) {
    return (
      <CarouselContainer>
        <ShimmerWrapper>
          {[...Array(isMobile ? 1 : 3)].map((_, index) => (
            <ShimmerSlide key={index} isMobile={isMobile}>
              <ShimmerContent />
            </ShimmerSlide>
          ))}
        </ShimmerWrapper>
        <PaginationDots>
          <Dot active={true} />
        </PaginationDots>
      </CarouselContainer>
    );
  }

  if (error || banners.length === 0) {
    return null; // silently hide if no promo banners configured yet
  }

  return (
    <CarouselContainer>
      <SlideTrack activeIndex={activeIndex} isMobile={isMobile}>
        {banners.map(banner => (
          <Slide key={banner.id} isMobile={isMobile}>
            <SlideContent
              hasLink={!!banner.link}
              onClick={() => handleBannerClick(banner.link)}
            >
              <SlideImage
                src={banner.image}
                alt={banner.type || 'Promo Banner'}
                loading="lazy"
              />
              {banner.link && <LinkBadge>Explore →</LinkBadge>}
            </SlideContent>
          </Slide>
        ))}
      </SlideTrack>

      <PaginationDots>
        {[...Array(totalSlides)].map((_, index) => (
          <Dot
            key={index}
            active={index === activeIndex}
            onClick={() => setActiveIndex(index)}
          />
        ))}
      </PaginationDots>
    </CarouselContainer>
  );
};

export default SkylaPromoSlider;
